import {
  CourierCapabilitiesDto,
  CourierDto,
  HubDto,
  KnownCourierApprovalStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { isCarrierProjectType } from '../../../../app/navigation/courier-project-type';
import { createPath, Paths } from '../../../../app/router/app.router';
import { CourierDetailsPageHeaderProps } from '../../../../ui/components/pure/page-headers/details/courier-details/courier-details-page-header.component';
import { CourierStatusBadgeDecision } from '../../../../ui/components/pure/page-headers/details/courier-details/courier-status-badge.component';
import { BadgeUserStatus } from '../../../../ui/components/pure/user-status-badge/badge-user-status';
import { userStatusToColor } from '../../../../ui/components/pure/user-status-badge/user-status-to-color.map';
import { userStatusToIcon } from '../../../../ui/components/pure/user-status-badge/user-status-to-icon.map';
import { userStatusToLabel } from '../../../../ui/components/pure/user-status-badge/user-status-to-label.map';
import { Unknown } from '../../../../ui/components/pure/icons/Unknown.icon';
import { Colors } from '../../../../ui/theme/colors';
import { CourierDetailsActions, makeDefineCourierDetailsActions } from '../actions/courier-details.actions';
import {
  courierApprovalDecisionLabels,
  courierApprovalDecisions,
  courierBadgeStatus
} from '../../logic/courier-form.map';
import { CourierBreadcrumbLinks, makeCourierBreadcrumbs } from './courier-breadcrumbs.map';
import { makeCourierNetworkCapabilities } from './courier-capabilities.map';
import { makeCourierRegistration } from './courier-registration.map';

/**
 * Everything but the capabilities comes off the one courier record. Capabilities are the exception
 * — nothing on the courier says what they can do at a kiosk, so they come from their own endpoint.
 */
export function makeCourierDetailsHeaderProps(
  courier: CourierDto,
  /** The project the page was reached through; actions are authorised against it. */
  projectID: string,
  setAction: (action: CourierDetailsActions) => void,
  capabilities?: CourierCapabilitiesDto,
  /** Whether the viewer may decide an approval; without it the badge has no menu. */
  canDecideApproval = false,
  /** The hub the courier registered at, off its own query. */
  registrationHub?: HubDto,
  /** Which pages the viewer can reach from the breadcrumbs; without either they are plain text. */
  breadcrumbAccess: CourierBreadcrumbAccess = {}
): CourierDetailsPageHeaderProps {
  const { breadcrumbs, carrierImage } = makeCourierBreadcrumbs(
    courier,
    makeCourierBreadcrumbLinks(courier, breadcrumbAccess)
  );
  const statusBadge = makeCourierStatusBadge(courier, setAction, canDecideApproval);

  return {
    name: courier.displayName || 'Courier',
    breadcrumbs,
    carrierImage,
    registration: makeCourierRegistration(
      courier.registration,
      // There is no hub detail page — the managing project's hubs list is the closest destination.
      registrationHub && {
        name: registrationHub.name,
        url: createPath(Paths.hubs, { projectID: registrationHub.managedByProjectID })
      }
    ),
    ...(statusBadge ? { status: statusBadge } : {}),
    mobile: courier.mobile,
    externalID: courier.externalID,
    username: courier.username,
    accessCode: courier.accessCode,
    image: courier.image,
    networkCapabilities: makeCourierNetworkCapabilities(capabilities),
    actions: makeDefineCourierDetailsActions(courier, projectID)(setAction)
  };
}

export type CourierBreadcrumbAccess = {
  /** The carrier pages of a pool courier's network, which are for internal users only. */
  carrierPages?: boolean;
  /** The couriers list of a company courier's own carrier or region project. */
  projectCouriers?: boolean;
};

/**
 * A company's courier (managed by a carrier or region project) links its carrier to that project's
 * couriers: it is the page the company's admins work in, and its carriers are better read as
 * rosters than as carrier pages. Unfiltered — a region holds the one carrier, and a carrier
 * project's list is its own couriers and its regions'. A pool courier (managed by the network) links
 * to the network's carrier pages instead. By shortcode where the record has one, else by ID — the
 * routes take either.
 */
function makeCourierBreadcrumbLinks(
  courier: CourierDto,
  access: CourierBreadcrumbAccess
): CourierBreadcrumbLinks | undefined {
  const { carrier, project } = courier;
  const projectID = project?.shortcode || project?.id;
  if (!projectID) {
    return undefined;
  }

  if (isCarrierProjectType(project?.projectType)) {
    if (!access.projectCouriers) {
      return undefined;
    }

    return { carrier: createPath(Paths.couriers, { projectID }) };
  }

  if (access.carrierPages) {
    return {
      carriers: createPath(Paths.carriers, { projectID }),
      carrier: carrier?.id ? createPath(Paths.carrier, { projectID, carrierID: carrier.id }) : undefined
    };
  }

  return undefined;
}

/**
 * The badge beside the name, badged as the list badges them — a rejection shows as a rejection
 * rather than the inactive it also is.
 *
 * Shown for every state, active included: it is the way into an approval decision, so it has to be
 * there for the couriers an operator wants to reject.
 */
function makeCourierStatusBadge(
  courier: Pick<CourierDto, 'status' | 'accessLevel'>,
  setAction: (action: CourierDetailsActions) => void,
  canDecideApproval: boolean
): CourierDetailsPageHeaderProps['status'] {
  const badgeStatus = courierBadgeStatus(courier) as BadgeUserStatus | undefined;

  if (!badgeStatus) {
    return undefined;
  }

  return {
    label: userStatusToLabel[badgeStatus] ?? badgeStatus,
    color: userStatusToColor[badgeStatus] ?? Colors.bookedScannedInProgressGray,
    Icon: userStatusToIcon[badgeStatus] ?? Unknown,
    decisions: makeApprovalDecisions(courier, setAction, canDecideApproval)
  };
}

/** The decisions the badge offers. None without permission, or the modal's save comes back 403. */
function makeApprovalDecisions(
  courier: Pick<CourierDto, 'status' | 'accessLevel'>,
  setAction: (action: CourierDetailsActions) => void,
  canDecideApproval: boolean
): CourierStatusBadgeDecision[] {
  if (!canDecideApproval) {
    return [];
  }

  return courierApprovalDecisions(courier).map((decision) => ({
    label: courierApprovalDecisionLabels[decision] ?? decision,
    onSelect: () =>
      setAction(
        decision === KnownCourierApprovalStatus.Approved
          ? CourierDetailsActions.EDIT_APPROVAL_APPROVE
          : CourierDetailsActions.EDIT_APPROVAL_REJECT
      )
  }));
}
